import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { ProductGrid } from '@/components/products/product-grid'
import { Button } from '@/components/ui/button'
import { BackButton } from '@/components/ui/back-button'
import { Store, MapPin, Star, Package, User, AlertCircle } from 'lucide-react'

export function SellerStorePage() {
  const { id } = useParams<{ id: string }>()
  const [store, setStore] = useState<any>(null)
  const [products, setProducts] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (id) {
      fetchStoreDetails()
      fetchStoreProducts()
    }
  }, [id])

  const fetchStoreDetails = async () => {
    try {
      const { data, error } = await supabase
        .from('seller_stores')
        .select(`
          *,
          seller:profiles!seller_id (
            id,
            full_name,
            email,
            avatar_url,
            rating,
            is_verified
          )
        `)
        .eq('seller_id', id)
        .single()

      if (error) throw error
      setStore(data)
    } catch (err) {
      console.error('Error fetching store details:', err)
      setError('Failed to load store details')
    }
  }

  const fetchStoreProducts = async () => {
    try {
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .eq('seller_id', id)
        .eq('status', 'active')
        .eq('is_deleted', false) // Filter out deleted products
        .order('created_at', { ascending: false })

      if (error) throw error
      setProducts(data || [])
    } catch (err) {
      console.error('Error fetching store products:', err)
      setError('Failed to load store products')
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        <div className="animate-pulse">
          <div className="h-32 bg-gray-200 rounded-lg mb-6"></div>
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2 mb-8"></div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="h-64 bg-gray-200 rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  if (error || !store) {
    return (
      <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
          <h2 className="text-lg font-medium text-red-800">
            {error || 'Store not found'}
          </h2>
          <p className="mt-2 text-sm text-red-600">
            We couldn't find the store you're looking for.
          </p>
          <Link to="/products">
            <Button variant="primary" className="mt-4">
              Browse All Products
            </Button>
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        <div className="mb-6">
          <BackButton to="/products" label="Back to Products" />
        </div>

        {/* Store Header */}
        <div className="bg-white rounded-lg shadow-lg overflow-hidden mb-8">
          <div className="bg-gradient-to-r from-primary to-blue-600 p-6 text-white">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between">
              <div className="flex items-center mb-4 md:mb-0">
                <div className="h-16 w-16 bg-white/20 rounded-full flex items-center justify-center">
                  <Store className="h-8 w-8 text-white" />
                </div>
                <div className="ml-4">
                  <div className="flex items-center">
                    <h1 className="text-2xl font-bold">{store.name}</h1>
                    {store.seller?.is_verified && (
                      <span className="ml-2 bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded-full">Verified</span>
                    )}
                  </div>
                  <div className="flex items-center mt-1">
                    <Star className="h-4 w-4 text-yellow-300" />
                    <span className="ml-1 text-white/90">{store.seller?.rating?.toFixed(1) || '0.0'}</span>
                  </div>
                </div>
              </div>
              <div className="flex flex-col">
                <div className="flex items-center text-white/90">
                  <MapPin className="h-4 w-4 mr-1" />
                  <span>{store.address || 'No address provided'}</span>
                </div>
                <div className="flex items-center mt-1 text-white/90">
                  <Package className="h-4 w-4 mr-1" />
                  <span>{products.length} products</span>
                </div>
              </div>
            </div>
          </div>
          
          {store.description && (
            <div className="p-6 border-t border-gray-200">
              <h2 className="text-lg font-medium text-gray-900 mb-2">About the Store</h2>
              <p className="text-gray-600">{store.description}</p>
            </div>
          )}
          
          {store.categories && store.categories.length > 0 && (
            <div className="px-6 pb-6">
              <div className="flex flex-wrap gap-2 mt-2">
                {store.categories.map((category: string) => (
                  <span 
                    key={category}
                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                  >
                    {category}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Products Section */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-6">Store Products</h2>
          
          {products.length === 0 ? (
            <div className="text-center py-12">
              <Package className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No products found</h3>
              <p className="mt-1 text-sm text-gray-500">
                This store doesn't have any active products at the moment.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-y-8 gap-x-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:gap-x-6">
              {products.map((product) => (
                <div key={product.id} className="group relative h-full flex flex-col">
                  <Link to={`/products/${product.id}`} className="block flex-grow">
                    <div className="aspect-square w-full overflow-hidden rounded-lg bg-gray-200">
                      <img
                        src={product.images?.[0] || "https://images.unsplash.com/photo-1523275335684-37898b6baf30"}
                        alt={product.title}
                        className="h-full w-full object-cover object-center group-hover:opacity-75 transition-opacity"
                      />
                    </div>
                    <div className="mt-4 flex justify-between">
                      <div className="max-w-[70%]">
                        <h3 className="text-sm text-gray-700 line-clamp-1">
                          {product.title}
                        </h3>
                        <p className="mt-1 text-sm text-gray-500 line-clamp-1">{product.category}</p>
                      </div>
                      <p className="text-sm font-medium text-gray-900">${product.price}</p>
                    </div>
                  </Link>
                  <div className="mt-4">
                    <Button
                      variant="primary"
                      size="sm"
                      className="w-full text-xs sm:text-sm flex items-center justify-center"
                      onClick={() => {
                        // Add to cart functionality would go here
                      }}
                    >
                      <span>Add to Cart</span>
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}